import { MessageCircleIcon } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useChatBranding, useChatStore } from '../ChatProvider';

interface ChatLauncherProps {
  className?: string;
}

export function ChatLauncher({ className }: ChatLauncherProps): ReactElement {
  const branding = useChatBranding();
  const setOpen = useChatStore((s) => s.setOpen);

  const tooltip = computeLauncherTooltip(
    branding.launcherTooltip ?? `Fale com o ${branding.title}`,
    branding.launcherTooltipMaxLength,
  );

  return (
    <div
      className={cn(
        'group fixed right-5 bottom-[max(env(safe-area-inset-bottom),1.25rem)] z-40 flex items-center gap-2',
        className,
      )}
    >
      {tooltip && (
        <div
          aria-hidden
          className="grid origin-right scale-95 grid-cols-[0fr] opacity-0 transition-all duration-200 ease-out group-hover:scale-100 group-hover:grid-cols-[1fr] group-hover:opacity-100"
        >
          <div className="overflow-hidden">
            <span className="bg-card text-card-foreground border-border block rounded-full border px-3.5 py-1.5 text-sm font-medium whitespace-nowrap shadow-md">
              {tooltip}
            </span>
          </div>
        </div>
      )}

      <span className="relative flex">
        <span
          aria-hidden
          className="bg-primary/40 absolute inset-0 rounded-full motion-safe:animate-ping motion-safe:[animation-duration:2.4s]"
        />
        <Button
          type="button"
          size="icon-lg"
          aria-label={`Abrir conversa com ${branding.title}`}
          data-slot="chat-launcher"
          onClick={() => {
            setOpen(true);
          }}
          className="shadow-primary/35 relative size-14 rounded-full shadow-xl transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircleIcon aria-hidden className="size-6" />
        </Button>
      </span>
    </div>
  );
}

function computeLauncherTooltip(raw: string, maxLength: number): string {
  const cap = Math.max(0, maxLength);
  if (cap === 0) return '';
  if (raw.length <= cap) return raw;
  if (cap === 1) return '…';
  return raw.slice(0, cap - 1) + '…';
}
